import { useState } from "react";
import { format } from "date-fns";
import { tr } from "date-fns/locale";
import { CalendarIcon, Send, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { api, SERVICES } from "@/lib/config";
import { cn } from "@/lib/utils";

const TIME_SLOTS = [
  "08:00", "09:00", "10:00", "11:00", "12:00", "13:00",
  "14:00", "15:00", "16:00", "17:00", "18:00", "19:00",
];

export default function AppointmentForm() {
  const [form, setForm] = useState({
    full_name: "",
    phone: "",
    service_type: "",
    appointment_time: "",
    address: "",
    note: "",
  });
  const [date, setDate] = useState(null);
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.phone || !form.service_type || !date || !form.appointment_time || !form.address) {
      toast.error("Lütfen tüm zorunlu alanları doldurun.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/appointments", {
        ...form,
        appointment_date: format(date, "yyyy-MM-dd"),
      });
      toast.success("Randevunuz başarıyla alındı! Kısa süre içinde sizi arayacağız.");
      setForm({
        full_name: "",
        phone: "",
        service_type: "",
        appointment_time: "",
        address: "",
        note: "",
      });
      setDate(null);
    } catch (err) {
      toast.error("Randevu gönderilemedi. Lütfen telefonla arayın.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="appointment" className="py-20 lg:py-32 bg-zinc-950 text-white relative overflow-hidden" data-testid="appointment-section">
      <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "radial-gradient(#fff 1px, transparent 1px)", backgroundSize: "24px 24px" }} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative grid lg:grid-cols-12 gap-10 lg:gap-16">
        <div className="lg:col-span-5">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF4500] mb-4">
            / Online Randevu
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter leading-[1.05]" data-testid="appointment-title">
            Dakikalar içinde randevu oluşturun.
          </h2>
          <p className="mt-6 text-zinc-400 text-base lg:text-lg leading-relaxed">
            Formu doldurun — bilgileriniz bize ulaşır ulaşmaz telefonla size dönerek randevu saatini
            kesinleştiriyoruz. Acil durumlarda lütfen doğrudan arayın.
          </p>
          <div className="mt-8 p-5 border border-white/10 rounded-sm bg-white/[0.02]">
            <div className="text-xs uppercase tracking-widest text-zinc-500 font-bold mb-1">Acil Durum Hattı</div>
            <a href="tel:+905386915938" className="text-2xl lg:text-3xl font-black text-white hover:text-[#FF4500] transition-colors" data-testid="appointment-side-call">
              0538 691 59 38
            </a>
          </div>
        </div>

        <form onSubmit={submit} className="lg:col-span-7 bg-white text-zinc-900 p-6 sm:p-8 lg:p-10 rounded-sm" data-testid="appointment-form">
          <div className="grid sm:grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label htmlFor="full_name" className="text-xs font-bold uppercase tracking-widest">Ad Soyad *</Label>
              <Input id="full_name" value={form.full_name} onChange={(e) => set("full_name", e.target.value)} className="rounded-sm h-11" placeholder="Ahmet Yılmaz" data-testid="input-full-name" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone" className="text-xs font-bold uppercase tracking-widest">Telefon *</Label>
              <Input id="phone" type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="rounded-sm h-11" placeholder="05xx xxx xx xx" data-testid="input-phone" />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label className="text-xs font-bold uppercase tracking-widest">Hizmet Türü *</Label>
              <Select value={form.service_type} onValueChange={(v) => set("service_type", v)}>
                <SelectTrigger className="rounded-sm h-11" data-testid="select-service">
                  <SelectValue placeholder="Hizmet seçiniz" />
                </SelectTrigger>
                <SelectContent>
                  {SERVICES.map((s) => (
                    <SelectItem key={s.id} value={s.title} data-testid={`service-option-${s.id}`}>
                      {s.title}
                    </SelectItem>
                  ))}
                  <SelectItem value="Diğer" data-testid="service-option-other">Diğer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-widest">Tarih *</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    className={cn("w-full h-11 rounded-sm justify-start text-left font-normal", !date && "text-zinc-500")}
                    data-testid="date-picker-trigger"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {date ? format(date, "dd MMMM yyyy", { locale: tr }) : "Tarih seçin"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={setDate}
                    disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                    initialFocus
                    locale={tr}
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-widest">Saat *</Label>
              <Select value={form.appointment_time} onValueChange={(v) => set("appointment_time", v)}>
                <SelectTrigger className="rounded-sm h-11" data-testid="select-time">
                  <SelectValue placeholder="Saat seçiniz" />
                </SelectTrigger>
                <SelectContent>
                  {TIME_SLOTS.map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="address" className="text-xs font-bold uppercase tracking-widest">Adres *</Label>
              <Input id="address" value={form.address} onChange={(e) => set("address", e.target.value)} className="rounded-sm h-11" placeholder="Mahalle, sokak, bina no, daire" data-testid="input-address" />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="note" className="text-xs font-bold uppercase tracking-widest">Not (isteğe bağlı)</Label>
              <Textarea id="note" value={form.note} onChange={(e) => set("note", e.target.value)} className="rounded-sm min-h-[90px]" placeholder="Arıza belirtisi, kombi markası vs." data-testid="input-note" />
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full mt-6 h-12 bg-[#FF4500] hover:bg-[#E63E00] rounded-sm font-bold text-base gap-2"
            data-testid="submit-appointment-btn"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
            {loading ? "Gönderiliyor..." : "Randevu Talebi Gönder"}
          </Button>
          <p className="text-xs text-zinc-500 mt-3 text-center">
            Bilgileriniz yalnızca randevu organizasyonu için kullanılır.
          </p>
        </form>
      </div>
    </section>
  );
}
